<script setup>
import { computed } from 'vue';
import { useDateFormat, formatCivilDate, isCivilDateLike } from '@/Composables/useDateFormat';

const props = defineProps({
    /**
     * The date value to format (string, Date, or timestamp)
     */
    value: {
        type: [String, Date, Number],
        default: null,
    },
    /**
     * Format type:
     * - 'date': Short date (15 ene 2024)
     * - 'datetime': Date and time (15 ene 2024, 14:30)
     * - 'time': Time only (14:30)
     * - 'long': Long date (15 de enero de 2024)
     * - 'month-year': Month and year only (enero 2024)
     * - 'full': Full date and time (15 de enero de 2024, 14:30)
     * - 'relative': Relative time (hace 5 min)
     * - 'input': For form inputs (2024-01-15)
     */
    format: {
        type: String,
        default: 'date',
        validator: (value) => ['date', 'datetime', 'time', 'long', 'month-year', 'full', 'relative', 'input'].includes(value),
    },
    /**
     * Fallback text when date is null/empty
     */
    fallback: {
        type: String,
        default: '-',
    },
    /**
     * HTML tag to render (span, time, div, etc.)
     */
    tag: {
        type: String,
        default: 'span',
    },
    /**
     * Show tooltip with full date on hover (auto-enabled for 'relative' format)
     */
    tooltip: {
        type: Boolean,
        default: null,
    },
    /**
     * Timezone source:
     * - 'user' (default): honor auth.user.timezone, fall back to browser TZ.
     *   Used for REM-254 PDF-style flows.
     * - 'browser': always use the browser's resolved TZ. Used for the CRM
     *   UI per REM-261; see also <AppLocalDate>.
     */
    source: {
        type: String,
        default: 'user',
        validator: (value) => ['user', 'browser'].includes(value),
    },
});

const {
    formatDate,
    formatDateTime,
    formatTime,
    formatLongDate,
    formatMonthYear,
    formatFullDateTime,
    formatRelative,
    formatInputDate,
} = useDateFormat({ source: props.source });

const formattedDate = computed(() => {
    if (!props.value) return props.fallback;

    // Civil-date wire format ("YYYY-MM-DD" or Laravel 'date' cast at UTC
    // midnight) — render without TZ to avoid shifting the day for users in
    // negative-offset zones. See REM-280 + the convention doc in CLAUDE.md.
    if (isCivilDateLike(props.value) && (props.format === 'date' || props.format === 'long')) {
        return formatCivilDate(props.value, { format: props.format === 'long' ? 'long' : 'short' });
    }

    switch (props.format) {
        case 'date':
            return formatDate(props.value);
        case 'datetime':
            return formatDateTime(props.value);
        case 'time':
            return formatTime(props.value);
        case 'long':
            return formatLongDate(props.value);
        case 'month-year':
            return formatMonthYear(props.value);
        case 'full':
            return formatFullDateTime(props.value);
        case 'relative':
            return formatRelative(props.value);
        case 'input':
            return formatInputDate(props.value);
        default:
            return formatDate(props.value);
    }
});

const tooltipText = computed(() => {
    if (!props.value) return null;

    // Show tooltip by default for relative dates, or when explicitly enabled
    const showTooltip = props.tooltip ?? props.format === 'relative';
    if (!showTooltip) return null;

    return formatFullDateTime(props.value);
});

const isoDate = computed(() => {
    if (!props.value) return null;
    const date = new Date(props.value);
    return isNaN(date.getTime()) ? null : date.toISOString();
});
</script>

<template>
    <component
        :is="tag"
        :title="tooltipText"
        :datetime="tag === 'time' ? isoDate : undefined"
    >
        {{ formattedDate }}
    </component>
</template>
